import { useCallback, useEffect, useState } from 'react';
import SearchInput from '@components/common/SearchInput';
import PageHeader from '@components/common/PageHeader';
import { api } from '@/api/weather/station/api.ts';
import {
    WeatherStationListItem,
    WeatherStationListRes,
    WeatherStationListReq,
} from '@/api/weather/station/type.ts';
import DataTable, {
    ColumnConfig,
    HeaderButton,
} from '@components/common/DataTable/DataTable.tsx';
import { Alert } from '@utils/alert';

const StationList = () => {
    const [filters, setFilters] = useState<WeatherStationListReq>(() => {
        return {
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            keyword: '',
        };
    });

    const updateFilter = (key: keyof WeatherStationListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const [stationRes, setStationListRes] = useState<WeatherStationListRes>();

    const fetchStation = useCallback(async () => {
        const response = await api.weatherStationList(filters);
        setStationListRes(response);
    }, [filters]);

    const columns: ColumnConfig<WeatherStationListItem>[] = [
        { key: 'stnIdx', header: 'No' },
        { key: 'stnId', header: '지점번호' },
        { key: 'stnKo', header: '지점명(한글)' },
        { key: 'stnEn', header: '지점명(영문)' },
        {
            key: 'lon',
            header: '경도',
            render: value => `${value}`,
        },
        {
            key: 'lat',
            header: '위도',
            render: value => `${value}`,
        },
        {
            key: 'ht',
            header: '해발고도',
            render: value => `${value}m`,
        },
        { key: 'regDt', header: '등록일자' },
    ];

    const headerButtons: HeaderButton[] = [
        {
            label: '기상대 데이터 갱신',
            icon: 'ri-refresh-line',
            className: 'btn btn-sm btn-primary btn-wave',
            onClick: async () => {
                await Alert.confirmAndExecute(
                    `기상대 데이터를 갱신하시겠습니까?`,
                    '최신기상대 데이터를 불러옵니다.',
                    true,
                    async () => {
                        await api.refreshWeatherStationList().then(() => {
                            fetchStation(); // 갱신
                        });
                    },
                );
            },
        },
    ];

    useEffect(() => {
        fetchStation();
    }, [fetchStation]);

    return (
        <>
            <PageHeader
                title="기상대관리"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '기상관리' },
                    { label: '기상대관리', active: true },
                ]}
            />

            <div className="row mt-4">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12">
                                    <SearchInput
                                        placeholder="지점번호, 지점명을 입력해주세요."
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    {stationRes && (
                        <DataTable<WeatherStationListItem>
                            data={stationRes?.data}
                            columns={columns}
                            headerButtons={headerButtons}
                            paging={stationRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default StationList;
